import React from 'react';
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import {HTMLContent, MultipleSelector, NumericValueField, contentUtils} from "mindsets-js-sdk";

import Matching from "./Matching";
import Fraction from "./Fraction";
import {SetQuestionPracticeSkillsUpdatedAction} from "../../actions/question_actions";
import Selector from "../../common/Selector";
import NumberOnly from "./NumberOnly";
import DragNDropPuzzle from "./DragNDropPuzzle";

function CorrectAnswer({question}) {
    const dispatch = useDispatch();
    const polls = useSelector(state => state.polls);
    const question_practice_skills_updated = useSelector(state => state.question_practice_skills_updated);
    const selected_assignment_index = useSelector(state => state.selected_assignment_index);
    const question_practice_skill = question_practice_skills_updated[selected_assignment_index];

    function onCorrectAnswerChange(correct_answer) {
        const question_practice_skills_updated_x = JSON.parse(JSON.stringify(question_practice_skills_updated));
        if (Array.isArray(correct_answer) && correct_answer.length > 0 || Object.isExtensible(correct_answer) && Object.keys(correct_answer).length > 0) {
            question_practice_skills_updated_x[selected_assignment_index].correct_answer = correct_answer;
        } else {
            question_practice_skills_updated_x[selected_assignment_index].correct_answer = null;
        }
        dispatch(SetQuestionPracticeSkillsUpdatedAction(question_practice_skills_updated_x));
    }

    function renderComponent() {
        if ((question.answer_type === 'answer.choice_single') || question.answer_type === 'answer.choice_multiple') {
            const options = Object.keys(question.answer_component_data.data.options).map(key => {
                return {
                    id: key,
                    label: <HTMLContent
                        html_str={contentUtils.processStringContent(question.answer_component_data.data.options[key])}/>
                }
            });

            return (
                <MultipleSelector
                    key={question.id + '-' + question_practice_skill.id}
                    item_name='option'
                    trigger={<div
                        className='bordered little'>{question_practice_skill.correct_answer && Array.isArray(question_practice_skill.correct_answer) && question_practice_skill.correct_answer.length > 0 ? question_practice_skill.correct_answer.map(i => 'Option ' + i).sort().join(', ') : 'Please select'}</div>}
                    items={options}
                    selected={question_practice_skill.correct_answer && Array.isArray(question_practice_skill.correct_answer) ? question_practice_skill.correct_answer : []}
                    callback={onCorrectAnswerChange}
                    show_all_option={false}
                    match_dropdown_width={false}
                />
            );
        } else if (question.answer_type === 'poll.inline') {
            const poll = polls.find(poll => poll.question_id === question.id);
            if (!poll) {
                return null;
            }
            const poll_new = JSON.parse(JSON.stringify(poll));

            return (
                <div className='d-flex flex-column flex-gap-5'>
                    {poll_new.poll_options.map(poll_option => {
                        let correct_answer = JSON.parse(JSON.stringify(question_practice_skill.correct_answer));
                        if (!Array.isArray(correct_answer)) {
                            correct_answer = [];
                        }
                        const correct_answer_option_index = correct_answer.findIndex(o => o.poll_option_id === poll_option.id);
                        const correct_answer_option = correct_answer[correct_answer_option_index];

                        const options = [];
                        options.push(<option key={'n'} value={''} disabled={true}>Please select</option>);
                        for (let i = 0; i <= question_practice_skill.max_points; i++) {
                            options.push(<option key={i} value={i}>{i}</option>);
                        }

                        return (
                            <div key={poll_option.id} className='d-flex flex-gap-5 align-items-center'>
                                <HTMLContent html_str={contentUtils.processStringContent(poll_option.option)}/>
                                <select
                                    className='form-control flex-shrink-1 w-auto'
                                    value={correct_answer_option && !isNaN(parseInt(correct_answer_option.points.toString())) ? parseInt(correct_answer_option.points.toString()) : ''}
                                    onChange={event => {
                                        const value = parseInt(event.target.value);
                                        if (correct_answer_option) {
                                            correct_answer[correct_answer_option_index].points = value
                                        } else {
                                            correct_answer.push({
                                                poll_option_id: poll_option.id,
                                                points: value
                                            });
                                        }
                                        onCorrectAnswerChange(correct_answer);
                                    }}
                                >{options}</select>
                            </div>
                        );
                    })}
                </div>
            );
        } else if (question.answer_type === 'answer.slider' || question.answer_type === 'answer.range' || question.answer_type === 'answer.number_line') {
            let correct_answer = !!question_practice_skill.correct_answer && !Array.isArray(question_practice_skill.correct_answer) && Object.isExtensible(question_practice_skill.correct_answer) ? JSON.parse(JSON.stringify(question_practice_skill.correct_answer)) : {};

            return (
                <div className='d-flex justify-content-between'>
                    <div>
                        <label className='form-label' htmlFor='from'>Min</label>
                        <NumericValueField
                            key={question.id + '-' + question_practice_skill.id}
                            callback={value => {
                                if (value === null) {
                                    delete correct_answer.min;
                                } else {
                                    correct_answer.min = value;
                                }
                                onCorrectAnswerChange(correct_answer);
                            }}
                            initial={correct_answer ? correct_answer.min : null}
                        />
                    </div>
                    <div>
                        <label className='form-label' htmlFor='to'>Max</label>
                        <NumericValueField
                            key={question.id + '-' + question_practice_skill.id}
                            callback={value => {
                                if (value === null) {
                                    delete correct_answer.max;
                                } else {
                                    correct_answer.max = value;
                                }
                                onCorrectAnswerChange(correct_answer);
                            }}
                            initial={correct_answer ? correct_answer.max : null}
                        />
                    </div>
                </div>
            );
        } else if (question.answer_type === 'answer.dnd') {
            let correct_answer = JSON.parse(JSON.stringify(question_practice_skill.correct_answer));
            if (!Array.isArray(correct_answer)) {
                correct_answer = [
                    {
                        text: '',
                        answer_layout: []
                    }
                ];
            }

            return (
                <div>
                    <button onClick={event => {
                        event.preventDefault();
                        correct_answer.push({
                            text: '',
                            answer_layout: []
                        });
                        onCorrectAnswerChange(correct_answer);
                    }}><i className='fas fa-plus'/></button>
                    <div className='d-flex flex-column flex-gap-10'>
                        {correct_answer.map((ca, i) => {
                            return (
                                <div
                                    key={i}
                                    className='d-flex flex-gap-10 bordered align-items-center justify-content-between'
                                >
                                    <input
                                        key={question.id + '-' + question_practice_skill.id + '-' + i}
                                        type="text"
                                        className="form-control"
                                        value={ca && ca.text ? ca.text : ''}
                                        onChange={event => {
                                            let correct_answer_option;
                                            const val = event.target.value;
                                            if (val) {
                                                correct_answer_option = {
                                                    text: val
                                                }
                                            } else {
                                                correct_answer_option = null;
                                            }

                                            correct_answer[i] = correct_answer_option;
                                            onCorrectAnswerChange(correct_answer);
                                        }}
                                    />
                                    <button className='red' onClick={event => {
                                        event.preventDefault();
                                        correct_answer.splice(i, 1)
                                        onCorrectAnswerChange(correct_answer);
                                    }}><i className='fas fa-trash red'/></button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else if (question.answer_type === 'answer.selector') {
            let existing_answer = undefined;
            if (question_practice_skill && !!question_practice_skill.correct_answer && !!question_practice_skill.correct_answer.answer_selections) {
                existing_answer = {
                    data: {
                        answer: question_practice_skill.correct_answer
                    }
                }
            }

            return (
                <div>
                    <Selector
                        key={question.id + '-' + question_practice_skill.id}
                        component_data={question.answer_component_data}
                        existing_answer={existing_answer}
                        callback={onCorrectAnswerChange}
                    />
                </div>
            );
        } else if (question.answer_type === 'answer.matching') {
            return (
                <div>
                    <Matching
                        key={question.id + '-' + question_practice_skill.id}
                        component_data={question.answer_component_data}
                        correct_answer={question_practice_skill.correct_answer ? question_practice_skill.correct_answer : null}
                        onCorrectAnswerChange={onCorrectAnswerChange}
                    />
                </div>
            );
        } else if (question.answer_type === 'answer.fraction') {
            let correct_answer = JSON.parse(JSON.stringify(question_practice_skill.correct_answer));
            if (!Array.isArray(correct_answer)) {
                correct_answer = [
                    {
                        text: '',
                        answer_selections: []
                    }
                ];
            }

            return (
                <div>
                    <button onClick={event => {
                        event.preventDefault();
                        correct_answer.push({
                            text: '',
                            answer_selections: []
                        });
                        onCorrectAnswerChange(correct_answer);
                    }}><i className='fas fa-plus'/></button>
                    <div className='d-flex flex-column flex-gap-10'>
                        {correct_answer.map((ca, i) => {
                            return (
                                <div
                                    key={i}
                                    className='d-flex flex-gap-10 bordered align-items-center justify-content-between'>
                                    <Fraction
                                        key={question.id + '-' + question_practice_skill.id + '-' + i}
                                        component_data={question.answer_component_data}
                                        correct_answer={ca}
                                        onCorrectAnswerChange={ca => {
                                            correct_answer[i] = ca;
                                            onCorrectAnswerChange(correct_answer);
                                        }}
                                    />
                                    <button className='red' onClick={event => {
                                        event.preventDefault();
                                        correct_answer.splice(i, 1)
                                        onCorrectAnswerChange(correct_answer);
                                    }}><i className='fas fa-trash red'/></button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else if (question.answer_type === 'answer.number_only') {
            let correct_answer = JSON.parse(JSON.stringify(question_practice_skill.correct_answer));
            if (!Array.isArray(correct_answer)) {
                correct_answer = [
                    {
                        text: '',
                        answer_selections: []
                    }
                ];
            }

            return (
                <div>
                    <button onClick={event => {
                        event.preventDefault();
                        correct_answer.push({
                            text: '',
                            answer_selections: []
                        });
                        onCorrectAnswerChange(correct_answer);
                    }}><i className='fas fa-plus'/></button>
                    <div className='d-flex flex-column flex-gap-10'>
                        {correct_answer.map((ca, i) => {
                            return (
                                <div
                                    key={i}
                                    className='d-flex flex-gap-10 bordered align-items-center justify-content-between'>
                                    <NumberOnly
                                        key={question.id + '-' + question_practice_skill.id + '-' + i}
                                        component_data={question.answer_component_data}
                                        correct_answer={ca}
                                        onCorrectAnswerChange={ca => {
                                            correct_answer[i] = ca;
                                            onCorrectAnswerChange(correct_answer);
                                        }}
                                    />
                                    <button className='red' onClick={event => {
                                        event.preventDefault();
                                        correct_answer.splice(i, 1)
                                        onCorrectAnswerChange(correct_answer);
                                    }}><i className='fas fa-trash red'/></button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else if (question.answer_type === 'answer.dnd_puzzle') {
            return (
                <div>
                    <DragNDropPuzzle
                        key={question.id + '-' + question_practice_skill.id}
                        component_data={question.answer_component_data}
                        correct_answer={question_practice_skill.correct_answer ? question_practice_skill.correct_answer : null}
                        onCorrectAnswerChange={onCorrectAnswerChange}
                    />
                </div>
            );
        }

        return (
            <div>This answer type does not accept Fixed Correct Answer at the moment.</div>
        );
    }

    if (question_practice_skill.mode === 'correct_answer') {
        return (
            <div className="my-4">
                <label>Correct answer</label>
                {renderComponent()}
            </div>
        );
    }

    return null;
}

CorrectAnswer.propTypes = {
    question: PropTypes.shape({
        id: PropTypes.number.isRequired,
        code: PropTypes.string.isRequired,
        answer_type: PropTypes.string.isRequired,
        options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        answer_component_data: PropTypes.object
    }).isRequired
}

export default CorrectAnswer;
